import { ArrowLeft, Check, Copy, ExternalLink, Globe, KeyRound, Mail, Tag } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  Badge,
  Card,
  Input,
  Button as ShButton,
} from '../../components/shadcn/index.js';
import { Button, FormField } from '../../components/ui/index.js';
import { TOGGLEABLE_MODULES, type ModuleKey } from '../../lib/modules.js';
import { platformApi } from '../../lib/platformApi.js';

import { LogoUpload } from './LogoUpload.js';

/** All modules start enabled; the operator unchecks the ones a tenant won't use. */
function allModulesOn(): Record<ModuleKey, boolean> {
  const o = {} as Record<ModuleKey, boolean>;
  for (const m of TOGGLEABLE_MODULES) o[m] = true;
  return o;
}

interface ProvisionResult {
  tenant: { id: string; name: string; slug: string };
  ownerUser: { id: string; email: string; fullName: string };
  tempPassword: string;
}

type Plan = 'free' | 'starter' | 'pro' | 'enterprise';

const PLAN_BLURB: Record<Plan, string> = {
  free: 'plans.blurb.free',
  starter: 'plans.blurb.starter',
  pro: 'plans.blurb.pro',
  enterprise: 'plans.blurb.enterprise',
};

// Mirrors PlatformTenantsPage / PlatformPaymentsPage so the preview's
// plan badge picks up the same tone the operator sees everywhere else.
const PLAN_VARIANT: Record<Plan, 'secondary' | 'info' | 'success' | 'default'> = {
  free: 'secondary',
  starter: 'info',
  pro: 'success',
  enterprise: 'default',
};

export function PlatformTenantCreatePage(): JSX.Element {
  const { t } = useTranslation('platform');
  const { t: tm } = useTranslation('settings');
  const navigate = useNavigate();
  const [modules, setModules] = useState<Record<ModuleKey, boolean>>(allModulesOn);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    defaultLocale: 'en' as 'en' | 'ar',
    baseCurrencyCode: 'USD',
    plan: 'free' as Plan,
    ownerEmail: '',
    ownerFullName: '',
    logoUrl: '',
    yearlyPriceAmount: '',
    initialDomain: '',
    initialDomainKind: 'storefront' as 'app' | 'storefront',
  });
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProvisionResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const slugSuggestion = useMemo(() => {
    if (form.slug) return form.slug;
    return form.name
      .toLowerCase()
      .replace(/['"]+/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
  }, [form.name, form.slug]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        ...form,
        slug: form.slug || slugSuggestion,
      };
      // Optional fields — omit when empty so the API uses its defaults
      // (14-day trial when no price; null logo) instead of failing
      // url-validation on an empty string.
      if (!form.logoUrl.trim()) delete payload.logoUrl;
      if (!form.yearlyPriceAmount.trim()) {
        delete payload.yearlyPriceAmount;
      } else {
        payload.yearlyPriceCurrencyCode = form.baseCurrencyCode;
      }
      // Optional initial custom domain — created `pending` (verify later from the
      // tenant detail page). Omit both when blank so the hostname regex isn't hit.
      if (!form.initialDomain.trim()) {
        delete payload.initialDomain;
        delete payload.initialDomainKind;
      } else {
        payload.initialDomain = form.initialDomain.trim().toLowerCase();
      }
      // Module on/off map — only sent when the operator disabled something
      // (all-enabled is the default, so we keep the payload clean).
      if (TOGGLEABLE_MODULES.some((m) => !modules[m])) payload.modules = modules;
      const r = await platformApi().post<ProvisionResult>('/tenants', payload);
      setResult(r.data);
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code ?? String((err as Error).message));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <SuccessPanel result={result} onContinue={() => navigate(`/platform/tenants/${result.tenant.id}`)} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <Link
          to="/platform/tenants"
          className="inline-flex items-center gap-1 text-xs text-fg-muted underline-offset-2 hover:text-fg hover:underline"
        >
          <ArrowLeft className="h-3 w-3 rtl:rotate-180" aria-hidden />
          {t('tenants.title')}
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">{t('create.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('create.subtitle')}</p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        {/* Left — the form */}
        <Card className="p-6">
          <form className="flex flex-col gap-5" onSubmit={onSubmit} noValidate>
            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-muted">
                {t('create.section.identity')}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField id="name" label={t('create.name')} required>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    required
                    autoFocus
                  />
                </FormField>
                <FormField id="slug" label={t('create.slug')} required>
                  <Input
                    id="slug"
                    value={form.slug}
                    onChange={(e) => update('slug', e.target.value.toLowerCase())}
                    required
                    pattern="^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$"
                    minLength={3}
                    maxLength={64}
                    placeholder={slugSuggestion || 'acme-co'}
                  />
                  <p className="mt-1 text-xs text-fg-muted">{t('create.slugHint')}</p>
                </FormField>
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-muted">
                {t('create.section.regional')}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField id="defaultLocale" label={t('create.defaultLocale')}>
                  <select
                    id="defaultLocale"
                    className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
                    value={form.defaultLocale}
                    onChange={(e) => update('defaultLocale', e.target.value as 'en' | 'ar')}
                  >
                    <option value="en">English</option>
                    <option value="ar">العربية</option>
                  </select>
                </FormField>
                <FormField id="baseCurrencyCode" label={t('create.baseCurrencyCode')}>
                  <Input
                    id="baseCurrencyCode"
                    value={form.baseCurrencyCode}
                    onChange={(e) => update('baseCurrencyCode', e.target.value.toUpperCase())}
                    maxLength={3}
                    pattern="^[A-Z]{3}$"
                  />
                </FormField>
                <FormField id="plan" label={t('create.plan')}>
                  <select
                    id="plan"
                    className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
                    value={form.plan}
                    onChange={(e) => update('plan', e.target.value as Plan)}
                  >
                    <option value="free">{t('tenants.plans.free')}</option>
                    <option value="starter">{t('tenants.plans.starter')}</option>
                    <option value="pro">{t('tenants.plans.pro')}</option>
                    <option value="enterprise">{t('tenants.plans.enterprise')}</option>
                  </select>
                </FormField>
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-muted">
                {t('create.section.owner')}
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField id="ownerEmail" label={t('create.ownerEmail')} required>
                  <Input
                    id="ownerEmail"
                    type="email"
                    value={form.ownerEmail}
                    onChange={(e) => update('ownerEmail', e.target.value)}
                    required
                  />
                </FormField>
                <FormField id="ownerFullName" label={t('create.ownerFullName')} required>
                  <Input
                    id="ownerFullName"
                    value={form.ownerFullName}
                    onChange={(e) => update('ownerFullName', e.target.value)}
                    required
                  />
                </FormField>
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-muted">
                {t('create.section.branding')}
              </h2>
              <div className="grid grid-cols-1 gap-5">
                <LogoUpload
                  label={t('create.logoUrl')}
                  value={form.logoUrl || null}
                  onChange={(next) => update('logoUrl', next ?? '')}
                />
                <FormField id="yearlyPriceAmount" label={t('create.yearlyPrice')}>
                  <div className="flex items-center gap-2">
                    <Input
                      id="yearlyPriceAmount"
                      value={form.yearlyPriceAmount}
                      onChange={(e) => update('yearlyPriceAmount', e.target.value)}
                      inputMode="decimal"
                      placeholder="588.00"
                      className="max-w-xs"
                    />
                    <span className="rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm text-fg-muted">
                      {form.baseCurrencyCode} / yr
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-fg-muted">{t('create.yearlyPriceHint')}</p>
                </FormField>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormField id="initialDomain" label={t('create.domain', { defaultValue: 'Custom domain (optional)' })}>
                    <Input
                      id="initialDomain"
                      value={form.initialDomain}
                      onChange={(e) => update('initialDomain', e.target.value)}
                      placeholder="shop.acme.com"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                    <p className="mt-1 text-xs text-fg-muted">{t('create.domainHint', { defaultValue: 'Added unverified — verify it later on the tenant page.' })}</p>
                  </FormField>
                  {form.initialDomain.trim() && (
                    <FormField id="initialDomainKind" label={t('domains.kind')}>
                      <select
                        id="initialDomainKind"
                        className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
                        value={form.initialDomainKind}
                        onChange={(e) => update('initialDomainKind', e.target.value as 'app' | 'storefront')}
                      >
                        <option value="storefront">{t('domains.kindOption.storefront')}</option>
                        <option value="app">{t('domains.kindOption.app')}</option>
                      </select>
                    </FormField>
                  )}
                </div>
              </div>
            </section>

            <section>
              <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-fg-muted">
                {t('create.section.modules', { defaultValue: 'Modules' })}
              </h2>
              <p className="mb-3 text-xs text-fg-muted">
                {t('create.modulesHint', {
                  defaultValue:
                    'Choose which optional modules this tenant gets. You can change these later on the tenant page.',
                })}
              </p>
              <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                {TOGGLEABLE_MODULES.map((m) => (
                  <label
                    key={m}
                    className="flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-bg-subtle"
                  >
                    <span className="min-w-0 truncate text-sm text-fg">
                      {tm(`modules.items.${m}.label`, { defaultValue: m })}
                    </span>
                    <input
                      type="checkbox"
                      className="h-5 w-5 flex-none accent-brand"
                      checked={modules[m]}
                      onChange={(e) => setModules((s) => ({ ...s, [m]: e.target.checked }))}
                      aria-label={tm(`modules.items.${m}.label`, { defaultValue: m })}
                    />
                  </label>
                ))}
              </div>
            </section>

            {error && (
              <p
                className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
                role="alert"
              >
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button type="submit" loading={submitting}>
                {t('create.submit')}
              </Button>
              <Link to="/platform/tenants">
                <Button type="button" variant="secondary">
                  {t('detail.actions.cancel')}
                </Button>
              </Link>
            </div>
          </form>
        </Card>

        {/* Right — live preview */}
        <Card className="self-start p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
            {t('create.preview.title')}
          </h3>

          {/* Tenant card mockup — what the workspace will look like in
              the sidebar header once provisioned. */}
          <div className="mt-4 flex items-center gap-3 rounded-md border border-border bg-bg-subtle p-3">
            {form.logoUrl ? (
              <img
                src={form.logoUrl}
                alt=""
                className="h-10 w-10 rounded object-cover ring-1 ring-border"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded bg-bg text-xs font-semibold text-fg-muted ring-1 ring-border">
                {(form.name || 'A').charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{form.name || t('create.preview.namePlaceholder')}</div>
              <div className="truncate text-xs text-fg-muted">{form.ownerEmail || '—'}</div>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-4">
            <PreviewRow
              icon={<Tag className="h-4 w-4" aria-hidden />}
              label={t('create.preview.name')}
              value={form.name || '—'}
            />
            <PreviewRow
              icon={<Globe className="h-4 w-4" aria-hidden />}
              label={t('create.preview.slug')}
              value={
                <code className="rounded bg-bg-subtle px-2 py-0.5 font-mono text-xs">
                  {slugSuggestion || '—'}
                </code>
              }
            />
            <PreviewRow
              icon={<Mail className="h-4 w-4" aria-hidden />}
              label={t('create.preview.owner')}
              value={form.ownerEmail || '—'}
            />
            <PreviewRow
              icon={<KeyRound className="h-4 w-4" aria-hidden />}
              label={t('create.preview.plan')}
              value={
                <Badge variant={PLAN_VARIANT[form.plan]}>{t(`tenants.plans.${form.plan}`)}</Badge>
              }
            />
            <PreviewRow
              icon={<Globe className="h-4 w-4" aria-hidden />}
              label={t('create.preview.regional')}
              value={`${form.defaultLocale.toUpperCase()} · ${form.baseCurrencyCode}`}
            />
            <PreviewRow
              icon={<KeyRound className="h-4 w-4" aria-hidden />}
              label={t('create.preview.billing')}
              value={
                form.yearlyPriceAmount.trim()
                  ? `${form.yearlyPriceAmount} ${form.baseCurrencyCode} / yr · ${t('create.preview.activeNow')}`
                  : t('create.preview.trial14')
              }
            />
          </div>
          <div className="mt-5 rounded-md border border-border-subtle bg-bg-subtle/40 p-3 text-xs text-fg-muted">
            {t('create.preview.note', { defaultValue: PLAN_BLURB[form.plan] ? '' : '' })}
            <p>{t(PLAN_BLURB[form.plan])}</p>
          </div>
        </Card>
      </div>
    </div>
  );

  function copyTemp(p: string) {
    void navigator.clipboard.writeText(p);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  function SuccessPanel({ result, onContinue }: { result: ProvisionResult; onContinue: () => void }) {
    return (
      <div className="flex flex-col gap-4">
        <header>
          <h1 className="text-2xl font-semibold">{t('create.successTitle')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('create.successHint')}</p>
        </header>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
          <Card className="p-6">
            <dl className="grid grid-cols-[12rem_1fr] gap-y-3 text-sm">
              <dt className="text-fg-muted">{t('create.name')}</dt>
              <dd className="font-medium">{result.tenant.name}</dd>
              <dt className="text-fg-muted">{t('create.slug')}</dt>
              <dd>
                <code className="rounded bg-bg-subtle px-2 py-0.5 font-mono text-xs">
                  {result.tenant.slug}
                </code>
              </dd>
              <dt className="text-fg-muted">{t('create.ownerEmail')}</dt>
              <dd className="font-medium">{result.ownerUser.email}</dd>
              <dt className="self-center text-fg-muted">{t('create.tempPassword')}</dt>
              <dd className="flex items-center gap-2">
                <code className="select-all rounded-md border border-warning/40 bg-warning/10 px-3 py-1.5 font-mono text-sm">
                  {result.tempPassword}
                </code>
                <ShButton size="sm" variant="outline" onClick={() => copyTemp(result.tempPassword)}>
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" />
                      {t('create.copied', { defaultValue: 'Copied' })}
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      {t('create.copy')}
                    </>
                  )}
                </ShButton>
              </dd>
            </dl>
            <div className="mt-6 flex gap-2">
              <Button onClick={onContinue}>
                <ExternalLink className="h-4 w-4" />
                {t('create.continueToTenant')}
              </Button>
              <Link to="/platform/tenants">
                <Button variant="secondary">{t('detail.actions.cancel')}</Button>
              </Link>
            </div>
          </Card>
          <Card className="self-start border-warning/40 bg-warning/5 p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-warning">
              {t('create.success.shareTitle', { defaultValue: 'Share with the owner' })}
            </h3>
            <ol className="mt-3 ms-4 list-decimal space-y-2 text-xs text-fg-muted">
              <li>{t('create.success.step1', { defaultValue: 'Copy the temporary password above.' })}</li>
              <li>{t('create.success.step2', { defaultValue: 'Send it to the owner via your secure channel — never plain email.' })}</li>
              <li>{t('create.success.step3', { defaultValue: 'They sign in at /login and will be prompted to change it.' })}</li>
            </ol>
          </Card>
        </div>
      </div>
    );
  }
}

function PreviewRow({
  icon, label, value,
}: { icon: React.ReactNode; label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 text-fg-muted">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[0.7rem] uppercase tracking-wide text-fg-muted">{label}</div>
        <div className="mt-0.5 text-sm font-medium">{value}</div>
      </div>
    </div>
  );
}
